import { httpsCallable } from 'firebase/functions';
import {
  collection,
  arrayUnion,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  type Unsubscribe
} from 'firebase/firestore';
import { ref as databaseRef, runTransaction } from 'firebase/database';
import { deleteObject, ref as storageRef } from 'firebase/storage';

import { database, db, functions, storage } from '@/modules/firebase';
import { getCurrentUser } from '@/modules/firebase/Auth';
import type { RecordFile } from '@/modules/common/files/FileClient';

export interface LegacyNcrFile {
  name: string;
  path: string;
  sourceFileId?: string;
}

export interface NcrRecord {
  id: string;
  newpart: string;
  process: boolean;
  quantity: number;
  identifier: string;
  severity: string;
  description: string;
  stage: string;
  ref: string;
  timelost: string;
  capa: string;
  issue: string;
  asana: string;
  status: string;
  disposition: string;
  ncr_shelf: boolean | string;
  datetime?: number;
  product?: string;
  username?: string;
  useremail?: string;
  files?: LegacyNcrFile[];
  fileSummary?: { readyCount?: number };
  [key: string]: unknown;
}

export interface NcrFormState {
  newpart: string;
  process: boolean;
  quantity: string;
  identifier: string;
  severity: string;
  description: string;
  stage: string;
  ref: string;
  timelost: string;
  capa: string;
  issue: string;
  asana: string;
  status: string;
  disposition: string;
  ncr_shelf: boolean;
}

export interface NcrPart {
  part: string;
  description: string;
  product?: string;
  type?: string;
  class?: string;
  vendors?: Array<{ vendorcode?: string }>;
}

export interface NcrIssue {
  id: string;
  title: string;
}

export interface NcrAuditEntry {
  id: string;
  field?: string;
  newValue?: unknown;
  oldValue?: unknown;
  timestamp?: unknown;
  type?: 'create' | 'edit';
  user?: string;
}

const DEFAULT_FORM: NcrFormState = {
  newpart: '',
  process: false,
  quantity: '1',
  identifier: '',
  severity: '',
  description: '',
  stage: '',
  ref: '',
  timelost: '',
  capa: '',
  issue: '',
  asana: '',
  status: 'Pending Review',
  disposition: '',
  ncr_shelf: true
};

function normalizeBoolean(value: unknown): boolean {
  return value === true || value === 'true' || value === 'on';
}

export function createEmptyNcrForm(): NcrFormState {
  return { ...DEFAULT_FORM };
}

export function formFromRecord(record: NcrRecord): NcrFormState {
  return {
    ...DEFAULT_FORM,
    newpart: String(record.newpart || ''),
    process: normalizeBoolean(record.process),
    quantity: String(record.quantity ?? 1),
    identifier: String(record.identifier || ''),
    severity: String(record.severity || ''),
    description: String(record.description || ''),
    stage: String(record.stage || ''),
    ref: String(record.ref || ''),
    timelost: String(record.timelost || ''),
    capa: String(record.capa || ''),
    issue: String(record.issue || ''),
    asana: String(record.asana || ''),
    status: String(record.status || 'Pending Review'),
    disposition: String(record.disposition || ''),
    ncr_shelf: normalizeBoolean(record.ncr_shelf)
  };
}

function recordFromForm(form: NcrFormState, parts: NcrPart[]): Partial<NcrRecord> {
  const profile = getCurrentUser();
  const matchedPart = parts.find(part => part.part === form.newpart);
  return {
    newpart: form.newpart.trim(),
    process: form.process,
    quantity: Math.min(9999, Math.max(1, Number.parseInt(form.quantity || '1', 10) || 1)),
    identifier: form.identifier.trim(),
    severity: form.severity,
    description: form.description.trim(),
    stage: form.stage,
    ref: form.ref.trim(),
    timelost: form.timelost.trim(),
    capa: form.capa.trim(),
    issue: form.issue,
    asana: form.asana.trim(),
    status: form.status,
    disposition: form.disposition,
    ncr_shelf: form.ncr_shelf ? 'true' : 'false',
    product: matchedPart?.product || '',
    username: profile?.displayName || '',
    useremail: profile?.email || ''
  };
}

function formatNcrId(value: number): string {
  return `NCR-${String(value).padStart(4, '0')}`;
}

export class NcrClient {
  private readonly printCallable = httpsCallable<any, { jobId?: string }>(functions, 'printNCRLabel');

  async loadParts(): Promise<NcrPart[]> {
    const cacheKey = 'parts';
    const cached = window.localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed?.value) && Number(parsed.timestamp || 0) > Date.now()) {
          return parsed.value;
        }
      } catch {
        window.localStorage.removeItem(cacheKey);
      }
    }

    const response = await fetch('https://us-central1-jidoka-voltera.cloudfunctions.net/api/partsearch');
    if (!response.ok) {
      throw new Error('Unable to load parts');
    }
    const parts = await response.json() as NcrPart[];
    window.localStorage.setItem(cacheKey, JSON.stringify({
      value: parts,
      timestamp: Date.now() + 864_000_000
    }));
    return parts;
  }

  async loadRecord(id: string): Promise<NcrRecord> {
    const snapshot = await getDoc(doc(db, 'ncr', id));
    if (!snapshot.exists()) {
      throw new Error(`${id} was not found`);
    }
    return { id: snapshot.id, ...snapshot.data() } as NcrRecord;
  }

  async loadIssues(): Promise<NcrIssue[]> {
    const snapshot = await getDocs(collection(db, 'issues'));
    return snapshot.docs
      .map(issue => ({ id: issue.id, title: String(issue.data().title || issue.id) }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  async create(form: NcrFormState, parts: NcrPart[]): Promise<NcrRecord> {
    const nextRef = databaseRef(database, 'nextrecord/ncr');
    const transaction = await runTransaction(nextRef, current => {
      const nextNumber = Number(current || 1);
      return nextNumber + 1;
    });
    const nextValue = Number(transaction.snapshot.val() || 2);
    const id = formatNcrId(nextValue - 1);
    const data = {
      ...recordFromForm(form, parts),
      id,
      datetime: Date.now(),
      files: []
    };
    await setDoc(doc(db, 'ncr', id), data);
    return { id, ...data } as NcrRecord;
  }

  async update(id: string, form: NcrFormState, parts: NcrPart[]): Promise<NcrRecord> {
    const data = recordFromForm(form, parts);
    await setDoc(doc(db, 'ncr', id), data, { merge: true });
    return this.loadRecord(id);
  }

  async removeLegacyFile(id: string, file: LegacyNcrFile, files: LegacyNcrFile[]): Promise<LegacyNcrFile[]> {
    const nextFiles = files.filter(entry => entry.name !== file.name || entry.path !== file.path);
    await deleteObject(storageRef(storage, file.path));
    await updateDoc(doc(db, 'ncr', id), {
      files: nextFiles.length ? nextFiles : deleteField()
    });
    return nextFiles;
  }

  async addModernFileMirror(id: string, file: RecordFile, downloadUrl: string): Promise<void> {
    await updateDoc(doc(db, 'ncr', id), {
      files: arrayUnion({
        name: file.displayName,
        path: downloadUrl,
        sourceFileId: file.id
      })
    });
  }

  async removeModernFileMirror(id: string, fileId: string, files: LegacyNcrFile[]): Promise<void> {
    const nextFiles = files.filter(file => file.sourceFileId !== fileId);
    await updateDoc(doc(db, 'ncr', id), {
      files: nextFiles.length ? nextFiles : deleteField()
    });
  }

  subscribeToRecord(id: string, onChange: (record: NcrRecord) => void): Unsubscribe {
    return onSnapshot(doc(db, 'ncr', id), snapshot => {
      if (snapshot.exists()) {
        onChange({ id: snapshot.id, ...snapshot.data() } as NcrRecord);
      }
    });
  }

  subscribeToAudit(id: string, onChange: (entries: NcrAuditEntry[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'ncr', id, 'lastmodified'), orderBy('timestamp', 'desc')),
      snapshot => onChange(snapshot.docs.map(entry => ({
        id: entry.id,
        ...entry.data()
      } as NcrAuditEntry)))
    );
  }

  async print(record: NcrRecord, parts: NcrPart[]): Promise<string> {
    const matchedPart = parts.find(part => part.part === record.newpart);
    const result = await this.printCallable({
      ncrId: record.id,
      itemDescription: matchedPart?.description || '',
      copies: 1
    });
    return result.data?.jobId || '';
  }
}

export const ncrClient = new NcrClient();
